import type { IntegrationConnection, Skill } from "@cohesive/api-client";
import { $createCodeNode } from "@lexical/code";
import {
  $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
} from "@lexical/list";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $createHeadingNode,
  $createQuoteNode,
  $isHeadingNode,
  HeadingTagType,
} from "@lexical/rich-text";
import { $setBlocksType } from "@lexical/selection";
import {
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  REDO_COMMAND,
  UNDO_COMMAND,
} from "lexical";
import {
  AtSign,
  Bold,
  Braces,
  Code,
  CodeSquare,
  Heading1,
  Heading2,
  Heading3,
  Italic,
  List,
  ListOrdered,
  Quote,
  Redo,
  ScrollText,
  Strikethrough,
  Undo,
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { IntegrationIcon } from "../../IntegrationIcon";
import { INSERT_PLACEHOLDER_COMMAND } from "../../MessageInput/Lexical/PlaceholderPlugin";
import { INSERT_SKILL_COMMAND } from "../../MessageInput/Lexical/SkillPlugin";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { Separator } from "../../ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../ui/tooltip";
export interface EditorToolbarProps {
  /** Skills for skill insertion */
  skills?: Skill[];
  /** Integration connections for entity insertion */
  integrationConnections?: IntegrationConnection[];
  /** Enable skills button */
  enableSkills?: boolean;
  /** Enable entities/connections button */
  enableEntities?: boolean;
  /** Enable placeholders button */
  enablePlaceholders?: boolean;
  /** Custom renderer for entity selection (allows user-dashboard to inject EntitySelector) */
  renderEntitySelector?: (props: {
    connection: IntegrationConnection;
    onClose: () => void;
    onBack: () => void;
  }) => React.ReactNode;
}

export const EditorToolbar: React.FC<EditorToolbarProps> = ({
  skills = [],
  integrationConnections = [],
  enableSkills = false,
  enableEntities = false,
  enablePlaceholders = false,
  renderEntitySelector,
}) => {
  const [editor] = useLexicalComposerContext();
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isCode, setIsCode] = useState(false);
  const [blockType, setBlockType] = useState<string>("paragraph");
  const [skillPopoverOpen, setSkillPopoverOpen] = useState(false);
  const [connectionPopoverOpen, setConnectionPopoverOpen] = useState(false);
  const [skillSearchQuery, setSkillSearchQuery] = useState("");
  const [connectionSearchQuery, setConnectionSearchQuery] = useState("");
  const [activeConnection, setActiveConnection] =
    useState<IntegrationConnection | null>(null);

  // Update toolbar state based on selection
  const updateToolbar = useCallback(() => {
    const selection = $getSelection();

    if ($isRangeSelection(selection)) {
      // Update text format
      setIsBold(selection.hasFormat("bold"));
      setIsItalic(selection.hasFormat("italic"));
      setIsStrikethrough(selection.hasFormat("strikethrough"));
      setIsCode(selection.hasFormat("code"));

      // Update block type
      const anchorNode = selection.anchor.getNode();
      const element =
        anchorNode.getKey() === "root"
          ? anchorNode
          : (anchorNode.getTopLevelElementOrThrow() as any);

      if ($isHeadingNode(element as any)) {
        setBlockType((element as any).getTag());
      } else if ($isListNode(element as any)) {
        const listType = (element as any).getListType();
        setBlockType(listType === "number" ? "ol" : "ul");
      } else {
        const nodeType = element.getType();
        setBlockType(nodeType === "quote" ? "quote" : "paragraph");
      }
    }
  }, []);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        updateToolbar();
      });
    });
  }, [editor, updateToolbar]);

  const formatHeading = (headingTag: HeadingTagType) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createHeadingNode(headingTag) as any);
      }
    });
  };

  const formatQuote = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createQuoteNode() as any);
      }
    });
  };

  const formatCode = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createCodeNode() as any);
      }
    });
  };

  const insertSkill = (skill: Skill) => {
    editor.dispatchCommand(INSERT_SKILL_COMMAND, {
      id: skill.id,
      name: skill.name,
    });
    setSkillPopoverOpen(false);
    setSkillSearchQuery("");
  };

  const selectConnectionForEntity = (connection: IntegrationConnection) => {
    setActiveConnection(connection);
  };

  const handleBackToConnections = () => {
    setActiveConnection(null);
  };

  const filteredSkills = useMemo((): typeof skills => {
    if (!skillSearchQuery.trim()) return skills;
    const query = skillSearchQuery.toLowerCase();
    return skills.filter((skill) => skill.name.toLowerCase().includes(query));
  }, [skills, skillSearchQuery]);

  // Filter connections based on search query
  const filteredConnections = useMemo(() => {
    if (!connectionSearchQuery.trim()) return integrationConnections;
    const query = connectionSearchQuery.toLowerCase();
    return integrationConnections.filter((conn) =>
      conn.integration_name.toLowerCase().includes(query),
    );
  }, [integrationConnections, connectionSearchQuery]);

  return (
    <TooltipProvider>
      <div className="flex items-center gap-1 p-2 border-b bg-muted/30 flex-wrap">
        {/* Undo/Redo */}
        <ToolbarButton
          onClick={() => editor.dispatchCommand(UNDO_COMMAND, undefined)}
          icon={<Undo className="w-4 h-4" />}
          label="Undo"
        />
        <ToolbarButton
          onClick={() => editor.dispatchCommand(REDO_COMMAND, undefined)}
          icon={<Redo className="w-4 h-4" />}
          label="Redo"
        />

        <Separator orientation="vertical" className="h-6 mx-1" />

        {/* Text Formatting */}
        <ToolbarButton
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold")}
          icon={<Bold className="w-4 h-4" />}
          label="Bold"
          active={isBold}
        />
        <ToolbarButton
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic")}
          icon={<Italic className="w-4 h-4" />}
          label="Italic"
          active={isItalic}
        />
        <ToolbarButton
          onClick={() =>
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, "strikethrough")
          }
          icon={<Strikethrough className="w-4 h-4" />}
          label="Strikethrough"
          active={isStrikethrough}
        />
        <ToolbarButton
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "code")}
          icon={<Code className="w-4 h-4" />}
          label="Inline Code"
          active={isCode}
        />

        <Separator orientation="vertical" className="h-6 mx-1" />

        {/* Headings */}
        <ToolbarButton
          onClick={() => formatHeading("h1")}
          icon={<Heading1 className="w-4 h-4" />}
          label="Heading 1"
          active={blockType === "h1"}
        />
        <ToolbarButton
          onClick={() => formatHeading("h2")}
          icon={<Heading2 className="w-4 h-4" />}
          label="Heading 2"
          active={blockType === "h2"}
        />
        <ToolbarButton
          onClick={() => formatHeading("h3")}
          icon={<Heading3 className="w-4 h-4" />}
          label="Heading 3"
          active={blockType === "h3"}
        />

        <Separator orientation="vertical" className="h-6 mx-1" />

        {/* Lists */}
        <ToolbarButton
          onClick={() =>
            editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
          }
          icon={<List className="w-4 h-4" />}
          label="Bullet List"
          active={blockType === "ul"}
        />
        <ToolbarButton
          onClick={() =>
            editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
          }
          icon={<ListOrdered className="w-4 h-4" />}
          label="Numbered List"
          active={blockType === "ol"}
        />

        <Separator orientation="vertical" className="h-6 mx-1" />

        {/* Block Elements */}
        <ToolbarButton
          onClick={formatQuote}
          icon={<Quote className="w-4 h-4" />}
          label="Quote"
          active={blockType === "quote"}
        />
        <ToolbarButton
          onClick={formatCode}
          icon={<CodeSquare className="w-4 h-4" />}
          label="Code Block"
          active={blockType === "code"}
        />

        {/* Insert Placeholder */}
        {enablePlaceholders && (
          <>
            <Separator orientation="vertical" className="h-6 mx-1" />
            <Tooltip delayDuration={300}>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 hover:bg-accent"
                  onClick={(e) => {
                    e.preventDefault();
                    editor.dispatchCommand(INSERT_PLACEHOLDER_COMMAND, {
                      placeholder: "",
                      value: "",
                      mode: "edit",
                      focus: true,
                    });
                  }}
                >
                  <Braces className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p className="text-xs">Add Placeholder</p>
              </TooltipContent>
            </Tooltip>
          </>
        )}

        {/* Skills */}
        {enableSkills && skills.length > 0 && (
          <>
            <Separator orientation="vertical" className="h-6 mx-1" />
            <Popover open={skillPopoverOpen} onOpenChange={setSkillPopoverOpen}>
              <Tooltip delayDuration={300}>
                <TooltipTrigger asChild>
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 hover:bg-accent"
                    >
                      <ScrollText className="w-4 h-4" />
                    </Button>
                  </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p className="text-xs">Skills</p>
                </TooltipContent>
              </Tooltip>
              <PopoverContent
                className="w-72 p-2"
                align="start"
                onCloseAutoFocus={(e) => {
                  e.preventDefault();
                }}
              >
                <div className="space-y-2">
                  <Input
                    placeholder="Search skills..."
                    value={skillSearchQuery}
                    onChange={(e) => setSkillSearchQuery(e.target.value)}
                    className="h-9 text-sm"
                  />
                  <div className="max-h-64 overflow-y-auto space-y-0.5">
                    {filteredSkills.length > 0 ? (
                      filteredSkills.map((skill) => (
                        <button
                          key={skill.id}
                          onClick={() => insertSkill(skill)}
                          className="w-full px-3 py-2 text-left text-sm rounded-lg hover:bg-accent transition-colors"
                        >
                          <div className="font-medium">{skill.name}</div>
                          {skill.description && (
                            <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                              {skill.description}
                            </div>
                          )}
                        </button>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        {skillSearchQuery
                          ? "No skills found"
                          : "No skills available"}
                      </p>
                    )}
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </>
        )}

        {/* Entities/Connections */}
        {enableEntities && integrationConnections.length > 0 && (
          <>
            <Separator orientation="vertical" className="h-6 mx-1" />
            <Popover
              open={connectionPopoverOpen}
              onOpenChange={(open) => {
                setConnectionPopoverOpen(open);
                if (!open) {
                  setActiveConnection(null);
                  setConnectionSearchQuery("");
                }
              }}
            >
              <Tooltip delayDuration={300}>
                <TooltipTrigger asChild>
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 hover:bg-accent"
                    >
                      <AtSign className="w-4 h-4" />
                    </Button>
                  </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p className="text-xs">Add Context</p>
                </TooltipContent>
              </Tooltip>
              <PopoverContent
                align={activeConnection ? "center" : "start"}
                sideOffset={8}
                collisionPadding={{
                  left: 20,
                  right: 20,
                  top: 20,
                  bottom: 20,
                }}
                className={activeConnection ? "w-[700px] p-0" : "w-72 p-2"}
                onCloseAutoFocus={(e) => {
                  e.preventDefault();
                }}
              >
                {activeConnection && renderEntitySelector ? (
                  <div className="h-[520px]">
                    {renderEntitySelector({
                      connection: activeConnection,
                      onClose: () => {
                        setActiveConnection(null);
                        setConnectionPopoverOpen(false);
                      },
                      onBack: handleBackToConnections,
                    })}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Input
                      placeholder="Search connections..."
                      value={connectionSearchQuery}
                      onChange={(e) => setConnectionSearchQuery(e.target.value)}
                      className="h-9 text-sm"
                    />
                    <div className="max-h-64 overflow-y-auto space-y-0.5">
                      {filteredConnections.length > 0 ? (
                        filteredConnections.map((connection) => (
                          <button
                            key={connection.id}
                            onClick={() =>
                              selectConnectionForEntity(connection)
                            }
                            className="w-full px-3 py-2 text-left text-sm rounded-lg hover:bg-accent transition-colors flex items-center gap-2"
                          >
                            <IntegrationIcon
                              platform={connection.integration_platform}
                              name={connection.integration_name}
                              imageURL={connection.integration_image_url}
                              className="h-4 w-4"
                            />
                            <span className="font-medium">
                              {connection.integration_name}
                            </span>
                          </button>
                        ))
                      ) : (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          {connectionSearchQuery
                            ? "No connections found"
                            : "No connections available"}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </PopoverContent>
            </Popover>
          </>
        )}
      </div>
    </TooltipProvider>
  );
};

interface ToolbarButtonProps {
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  active?: boolean;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({
  onClick,
  icon,
  label,
  active = false,
}) => {
  return (
    <Tooltip delayDuration={300}>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClick}
          className={`h-8 w-8 p-0 ${active ? "bg-accent" : ""}`}
        >
          {icon}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom">
        <p className="text-xs">{label}</p>
      </TooltipContent>
    </Tooltip>
  );
};
